import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API_CONFIG } from '@/services/api';
import { useResearchStore } from '@/stores/researchStore';

type GuideMessage = {
  role: 'assistant' | 'user';
  content: string;
};

type GuideStep = {
  title: string;
  body: string;
};

type GuideContent = {
  title: string;
  intro: string;
  tips: string[];
  tour: GuideStep[];
};

const GUIDE_FALLBACK: Record<string, GuideContent> = {
  search: {
    title: 'Search Guide',
    intro: 'Use Search to find leads fast, then enrich them with Get Data or Super Search. Keep filters tight, validate a few results, then scale.',
    tips: [
      'Start narrow: role + location + seniority. Widen once quality is good.',
      'Get Data saves contacts; Super Search adds LinkedIn + website in one run.',
      'Use bulk actions after selecting rows.',
      'If results are empty, loosen 1 filter at a time (location → title → company size).',
    ],
    tour: [
      { title: 'Step 1: Build your query', body: 'Enter role, company, and location filters. Keep it narrow first.' },
      { title: 'Step 2: Run search', body: 'Review the list for quality before enriching.' },
      { title: 'Step 3: Get Data', body: 'Save selected contacts into My Lists.' },
      { title: 'Step 4: Super Search', body: 'Run contact + LinkedIn + website in one flow for deeper enrichment.' },
    ],
  },
  lists: {
    title: 'My Lists Guide',
    intro: 'Go to My Lists → Contacts to see every saved contact. Use search to filter by name, company, or title, then open a contact to view the full report. Use Export to download the filtered list. Switch to Companies to see grouped companies.',
    tips: [
      'Filters: List shows only contacts in that saved list.',
      'Filters: Search matches name, company, title, and derived company.',
      'Re‑run refreshes a contact using current sources. Use it when data is stale.',
      'LinkedIn scrapes reveal “open to work” and recent signals faster than job change notices.',
      'Gray social icons mean no data yet. Colored icons mean data exists or a report is available.',
      'Social scraping warning is valid: we use lightweight signals like name and location; social sites often return many matches and make bot searches hard.',
      'Automation and upgrades require a quick meeting with our team.',
    ],
    tour: [
      { title: 'Step 1: Pick Contacts or Companies', body: 'Switch tabs from the header dropdown.' },
      { title: 'Step 2: Filter', body: 'Use List + Search to narrow by name, company, or title.' },
      { title: 'Step 3: Open Reports', body: 'Open a contact to view LinkedIn and social data.' },
      { title: 'Step 4: Export', body: 'Export a filtered set for outreach.' },
    ],
  },
  content: {
    title: 'Content Scraping Guide',
    intro: 'Use Content Scraping to collect social posts or ad data. Start small, confirm quality, then scale.',
    tips: [
      'Start with one platform before expanding.',
      'Check status in the status bar after starting scrapes.',
      'Results appear in your saved data views.',
      'If a scrape fails, verify the URL and try again.',
    ],
    tour: [
      { title: 'Step 1: Choose a scraper', body: 'Pick Social Media Posts or Ad Library.' },
      { title: 'Step 2: Provide target', body: 'Enter the URL or keyword set.' },
      { title: 'Step 3: Run & monitor', body: 'Watch the status bar and open results.' },
    ],
  },
  profitpod: {
    title: 'ProfitPod Guide',
    intro: 'ProfitPod helps you find and analyze podcasts.',
    tips: [
      'Start with Rephonic search for accurate matches.',
      'Use scraping to enrich a shortlist.',
      'Combine with My Lists for outreach workflows.',
    ],
    tour: [
      { title: 'Step 1: Search podcasts', body: 'Use Rephonic to find a tight list.' },
      { title: 'Step 2: Scrape details', body: 'Run scrapes to capture episode and contact info.' },
      { title: 'Step 3: Save results', body: 'Move relevant podcasts into your lists.' },
    ],
  },
  business: {
    title: 'Business Intel Guide',
    intro: 'Business Intel gives you market and company context fast.',
    tips: [
      'Use each tab for a different segment (Recruiters, Investors, Funding, Ecommerce).',
      'Open company details to see reports and links.',
      'Pair with Search for outreach targeting.',
    ],
    tour: [
      { title: 'Step 1: Choose a segment', body: 'Pick the Business Intel tab that matches your use case.' },
      { title: 'Step 2: Filter companies', body: 'Use filters to narrow results.' },
      { title: 'Step 3: Enrich', body: 'Run reports on top targets.' },
    ],
  },
  booking: {
    title: 'Booking AI Agent Guide',
    intro: 'Use Booking Agent to generate outreach-ready responses and book meetings faster.',
    tips: [
      'Start with a clear outcome (book call, follow-up, re‑engage).',
      'Provide context: company, role, and reason to reach out.',
      'Keep prompts short and specific for best results.',
    ],
    tour: [
      { title: 'Step 1: Pick a scenario', body: 'Choose the outreach type you want.' },
      { title: 'Step 2: Add context', body: 'Add details about the lead and your offer.' },
      { title: 'Step 3: Generate', body: 'Use the output directly or tweak it for tone.' },
    ],
  },
  grades: {
    title: 'Grades Guide',
    intro: 'Grades help you prioritize leads based on fit and intent signals.',
    tips: [
      'Run grades on your best segments first.',
      'Use grades to prioritize outreach queues.',
      'Re‑grade after you refresh data.',
    ],
    tour: [
      { title: 'Step 1: Select leads', body: 'Choose the segment you want scored.' },
      { title: 'Step 2: Run grading', body: 'Start grading and monitor progress.' },
      { title: 'Step 3: Act', body: 'Use grades to focus on top leads first.' },
    ],
  },
  troubleshoot: {
    title: 'Troubleshooting Guide',
    intro: 'If something fails, here are the fastest fixes.',
    tips: [
      'Hard refresh after deploys.',
      'If results are empty, loosen filters.',
      'If a scrape fails, re‑run with a clean URL.',
      'If a report fails to load, re‑open it after a few seconds.',
    ],
    tour: [
      { title: 'Step 1: Verify inputs', body: 'Check URLs, filters, and required fields.' },
      { title: 'Step 2: Re‑run', body: 'Try a single record to isolate the issue.' },
      { title: 'Step 3: Escalate', body: 'Capture the error and share it for debugging.' },
    ],
  },
  default: {
    title: 'Guide',
    intro: 'I can help you find features, run scrapes, and get results. Ask about Search, My Lists, Content Scraping, Booking Agent, Grades, or troubleshooting.',
    tips: [
      'Tell me what you’re trying to do and I’ll point you to the right page.',
      'Use Search to find leads, then save them to My Lists.',
      'Ask for a guided tour if you want a quick walkthrough.',
    ],
    tour: [
      { title: 'Step 1: Start at Search', body: 'Find leads and run Get Data.' },
      { title: 'Step 2: Review in My Lists', body: 'Open contacts/companies for saved results.' },
      { title: 'Step 3: Enrich with scrapes', body: 'Use Content Scraping or Super Search.' },
    ],
  },
};

const GUIDE_ROUTE_MAP: Array<{ key: keyof typeof GUIDE_FALLBACK; paths: string[] }> = [
  { key: 'search', paths: ['/lead-search', '/search'] },
  { key: 'lists', paths: ['/my-lists', '/my-lists/contacts', '/my-lists/companies', '/my-lists/podcasts'] },
  { key: 'content', paths: ['/contact-socials', '/ad-data'] },
  { key: 'profitpod', paths: ['/podcasts'] },
  { key: 'booking', paths: ['/booking-ai-agent'] },
  { key: 'grades', paths: ['/grades'] },
  { key: 'business', paths: ['/Recruiting', '/Investors', '/Capital', '/Socials'] },
];

const STORAGE_KEY = 'guide-bot-state-v1';
const GUIDE_CONTEXT_STORAGE_KEY = 'pepai-guide-context-v1';
const GUIDE_MODAL_WIDTH = 520;

function compactFilterContext(filters: unknown): Record<string, unknown> {
  if (!filters || typeof filters !== 'object') return {};
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      const normalized = value
        .map((entry) => String(entry ?? '').trim())
        .filter(Boolean);
      if (normalized.length > 0) out[key] = normalized;
      continue;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) out[key] = trimmed;
      continue;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      out[key] = value;
      continue;
    }
    if (value === true) {
      out[key] = true;
    }
  }
  return out;
}

function readAiGuideContext(): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(GUIDE_CONTEXT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function formatGuideMessage(content: string): string {
  const raw = String(content ?? '');
  const normalized = raw
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')
    .replace(/\\"/g, '"')
    .replace(/\\\//g, '/')
    .replace(/\r\n?/g, '\n');

  return normalized
    .replace(/^\s{0,3}#{1,6}\s*/gm, '')
    .replace(/^\s*[-*]\s+/gm, '• ')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\[(\d+)\]/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function GuideBot() {
  const location = useLocation();
  const searchFilters = useResearchStore((state) => state.filters);
  const [isOpen, setIsOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [messages, setMessages] = useState<GuideMessage[]>([]);
  const [input, setInput] = useState('');
  const [isTouring, setIsTouring] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [loadingReply, setLoadingReply] = useState(false);

  const pageKey = useMemo(() => {
    const match = GUIDE_ROUTE_MAP.find((item) => item.paths.includes(location.pathname));
    return match?.key ?? 'default';
  }, [location.pathname]);

  const guideContent = useMemo(() => GUIDE_FALLBACK[pageKey] ?? GUIDE_FALLBACK.default, [pageKey]);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (typeof parsed?.hidden === 'boolean') setIsHidden(parsed.hidden);
      if (typeof parsed?.open === 'boolean') setIsOpen(parsed.open);
    } catch {}
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ hidden: isHidden, open: isOpen }));
  }, [isHidden, isOpen]);

  useEffect(() => {
    setMessages([{ role: 'assistant', content: guideContent.intro }]);
    setIsTouring(false);
    setTourStep(0);
  }, [guideContent.intro]);

  const startTour = () => {
    setIsTouring(true);
    setTourStep(0);
  };

  const handleSend = async () => {
    const prompt = input.trim();
    if (!prompt) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: prompt }]);

    const fallback = (() => {
      const lower = prompt.toLowerCase();
      if (lower.includes('tour')) return `Sure — click “Start Tour” to walk through ${guideContent.title}.`;
      if (lower.includes('search')) return GUIDE_FALLBACK.search.intro;
      if (lower.includes('list')) return GUIDE_FALLBACK.lists.intro;
      if (lower.includes('company')) return 'Companies are derived from contacts. Use the Companies tab in My Lists and open drawers to view reports.';
      if (lower.includes('contact')) return 'Contacts live in My Lists → Contacts. Use search + filters, then open drawers for reports.';
      if (lower.includes('linkedin')) return 'Run LinkedIn scrapes from Contacts, then open the report drawer to view profile + posts.';
      if (lower.includes('scrape') || lower.includes('social') || lower.includes('ad')) return GUIDE_FALLBACK.content.intro;
      if (lower.includes('booking')) return GUIDE_FALLBACK.booking.intro;
      if (lower.includes('grade')) return GUIDE_FALLBACK.grades.intro;
      if (lower.includes('podcast')) return GUIDE_FALLBACK.profitpod.intro;
      if (lower.includes('trouble') || lower.includes('error') || lower.includes('fail')) return GUIDE_FALLBACK.troubleshoot.intro;
      if (lower.includes('icp')) return 'ICP setup lives in Search filters. Start with title + location + seniority.';
      return guideContent.intro;
    })();

    const guideApi = (API_CONFIG as any).GUIDE_API as string | undefined;
    if (!guideApi) {
      setMessages((prev) => [...prev, { role: 'assistant', content: fallback }]);
      return;
    }

    setLoadingReply(true);
    try {
      const response = await fetch(guideApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          page: pageKey,
          path: location.pathname,
          context: {
            searchFilters: compactFilterContext(searchFilters),
            aiEngagement: readAiGuideContext(),
          },
        }),
      });
      if (!response.ok) throw new Error('Guide API failed');
      const data = await response.json();
      const reply = typeof data?.reply === 'string' ? data.reply : fallback;
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', content: fallback }]);
    } finally {
      setLoadingReply(false);
    }
  };

  if (isHidden) {
    return (
      <div className="fixed z-50 left-4 bottom-4">
        <button
          onClick={() => {
            setIsHidden(false);
            setIsOpen(true);
          }}
          className="px-3 py-2 rounded-full bg-red-600 hover:bg-red-500 text-white text-sm shadow-lg"
        >
          Guide
        </button>
      </div>
    );
  }

  return (
    <div className="fixed z-50 left-4 bottom-4">
      <div className={`rounded-xl border border-gray-800 bg-black shadow-2xl ${isOpen ? '' : 'overflow-hidden'}`} style={{ width: GUIDE_MODAL_WIDTH, maxWidth: 'calc(100vw - 16px)' }}>
        <div
          className="flex items-center justify-between px-3 py-2 border-b border-gray-800 bg-gray-950"
        >
          <div className="text-xs uppercase tracking-wide text-gray-400">Guide</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsOpen((v) => !v)}
              className="text-xs text-gray-300 hover:text-white"
            >
              {isOpen ? 'Minimize' : 'Open'}
            </button>
            <button
              onClick={() => {
                setIsHidden(true);
                setIsOpen(false);
              }}
              className="text-xs text-gray-500 hover:text-gray-300"
            >
              Hide
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="p-3 space-y-3">
            <div className="space-y-1">
              <div className="text-white font-semibold text-sm">{guideContent.title}</div>
              <div className="text-xs text-gray-400">{guideContent.intro}</div>
            </div>

            <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
              {messages.map((msg, idx) => (
                <div
                  key={`${msg.role}-${idx}`}
                  className={`text-xs px-2 py-2 rounded-lg ${
                    msg.role === 'assistant' ? 'bg-gray-900 text-gray-200' : 'bg-red-600/20 text-red-200'
                  }`}
                >
                  <div className="whitespace-pre-wrap break-words select-text">{formatGuideMessage(msg.content)}</div>
                </div>
              ))}
              {loadingReply && (
                <div className="text-xs text-gray-500">Thinking…</div>
              )}
            </div>

            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSend();
                }}
                placeholder="Ask how to use this page..."
                className="flex-1 px-2 py-2 bg-gray-900 border border-gray-800 rounded-lg text-xs text-white"
              />
              <button
                onClick={handleSend}
                className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-xs text-white"
              >
                Send
              </button>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={startTour}
                className="text-xs text-gray-300 hover:text-white"
              >
                Start Tour
              </button>
              <button
                onClick={() => setIsTouring((v) => !v)}
                className="text-xs text-gray-500 hover:text-gray-300"
              >
                {isTouring ? 'Hide Tour' : 'Show Tour'}
              </button>
            </div>

            {isTouring && (
              <div className="space-y-2">
                <div className="text-xs text-gray-400">
                  Step {tourStep + 1} / {guideContent.tour.length}
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-2">
                  <div className="text-sm text-white font-semibold">{guideContent.tour[tourStep].title}</div>
                  <div className="text-xs text-gray-400">{guideContent.tour[tourStep].body}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setTourStep((v) => Math.max(0, v - 1))}
                    disabled={tourStep === 0}
                    className="text-xs px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-40"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setTourStep((v) => Math.min(guideContent.tour.length - 1, v + 1))}
                    disabled={tourStep >= guideContent.tour.length - 1}
                    className="text-xs px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            <div className="border-t border-gray-800 pt-2">
              <div className="text-xs text-gray-500 mb-2">Quick Tips</div>
              <div className="space-y-1">
                {guideContent.tips.map((tip) => (
                  <div key={tip} className="text-xs text-gray-300">
                    {tip}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
